import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class HTTPNode extends BaseNode {
    constructor() {
        super();
        this.name = 'HTTP Request';
        this.id = 'node_http';
        this.linkedFeature = 'http';
        this.icon = 'MdOutlineHttp';
        this.fileLinks = [];
        this.files = [];
        this.requirements = ['network'];
        this.isAsync = true;

        this.inputs = [
            {
                name: 'Request',
                portId: 'http_request_start',
                params: [
                    {
                        name: 'data',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];

        this.outputs = [
            {
                name: 'Data Arrival',
                portId: 'http_data_arrival',
                params: [
                    {
                        name: 'data',
                        dataType: TibboDataType.STRING,
                    },
                ],
            },
            {
                name: 'Request Complete',
                portId: 'http_complete',
                params: [],
            },
            {
                name: 'Error',
                portId: 'http_error',
                params: [
                    {
                        name: 'error_message',
                        dataType: TibboDataType.STRING,
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'URL',
                name: 'URL',
                settingType: SettingType.STRINGVARIABLE,
                dataType: TibboDataType.STRING,
                helpText: `
The complete URL to send the request to (including the protocol, domain, and path e.g. http://example.com/endpoint1).
                `,
                required: true,
                default: '',
            },
            {
                id: 'METHOD',
                name: 'HTTP Method',
                settingType: SettingType.OPTION,
                required: true,
                default: 'HTTP_GET',
                options: [
                    {
                        name: 'GET',
                        value: 'HTTP_GET',
                    },
                    {
                        name: 'HEAD',
                        value: 'HTTP_HEAD',
                    },
                    {
                        name: 'POST',
                        value: 'HTTP_POST',
                    },
                    {
                        name: 'PUT',
                        value: 'HTTP_PUT',
                    },
                    {
                        name: 'DELETE',
                        value: 'HTTP_DELETE',
                    },
                    {
                        name: 'PATCH',
                        value: 'HTTP_PATCH',
                    },
                ],
            },
            // {
            //     id: 'CONTENT_TYPE',
            //     name: 'Content Type',
            //     settingType: SettingType.OPTION,
            //     required: true,
            //     default: 'text/plain',
            //     options: [
            //         {
            //             name: 'text/plain',
            //             value: 'text/plain',
            //         },
            //         {
            //             name: 'application/json',
            //             value: 'application/json',
            //         },
            //     ],
            // },
            {
                id: 'HEADERS',
                name: 'Headers',
                settingType: SettingType.ARRAY,
                refId: 'http_header',
                required: true,
                default: [],
            },
            {
                id: 'CERTIFICATEFILE',
                name: 'SSL Certificate',
                settingType: SettingType.FILE,
                required: true,
                default: '',
                max: 102400,
            },
            // {
            //     id: 'DATA',
            //     name: 'Data',
            //     settingType: SettingType.STRINGVARIABLE,
            //     required: true,
            //     default: '',
            // },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies(): string[] {
        const dependencies = ['http'];
        if (this.project.runtime === 'zephyr') {
            const URL = this.getSettingValue('URL').trim();
            if (URL.startsWith('https') || URL.startsWith('${')) {
                dependencies.push('mbedtls');
            }
        }
        return dependencies;
    }

    // validate(): ValidationMessage[] {
    //     const errors: ValidationMessage[] = [];
    //     const project: ProjectConfiguration = this.project;
    //     let links = project.getNodeLinks(this, 'http_request_start');
    //     if (links.length > 0) {
    //         const link = links[0];
    //         const dataParameter = this.project.getNodeParameter(link, 'data');
    //         const ARGUMENTS = this.getSettingValue('ARGUMENTS');
    //         if (ARGUMENTS.length > 0) {
    //             if (dataParameter !== '') {
    //                 errors.push({
    //                     messageType: ValidationMessageType.UPDATE,
    //                     message: '',
    //                     featureId: this.getID(),
    //                     settingId: 'DATA',
    //                     data: dataParameter,
    //                 });
    //             }
    //             errors.push({
    //                 messageType: ValidationMessageType.UPDATE,
    //                 message: '',
    //                 featureId: this.getID(),
    //                 settingId: 'ARGUMENTS',
    //                 data: [],
    //             });
    //         }
    //     }
    //     return errors;
    // }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const URL = this.getSettingValue('URL');
            return `HTTP ${this.getSettingValueOptionName('METHOD')} request to "${URL}"`;
        }
        return super.getSettingValue(settingId);
    }

    getState(settingId: string): SettingState {
        if (settingId === 'CERTIFICATEFILE') {
            if (this.project.runtime === 'micropython') {
                return SettingState.HIDDEN;
            }
        }
        return super.getState(settingId);
    }

    getFiles() {
        const CERTIFICATEFILE = this.getSettingValue('CERTIFICATEFILE');
        const fileArray = [];
        if (CERTIFICATEFILE !== '') {
            fileArray.push(CERTIFICATEFILE.split('\n')[0]);
        }
        return this.files.concat(fileArray);
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `##### Request
Make an HTTP request to a specified URL with parameters.`;
    }
}
